import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
    UserCircleIcon,
    PhoneIcon,
    PencilIcon,
    TruckIcon,
    BuildingStorefrontIcon,
    GlobeAltIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { getCountryLabel, getDisplayPhone } from '../utils/phoneUtils';
import { usePageScroll } from '../hooks/usePageScroll';

export const Profile: React.FC = () => {
    usePageScroll('profile');

    const navigate = useNavigate();
    const location = useLocation();

    // Берём данные из AuthContext — он уже всё загрузил, getSession не нужен
    const { user, loading, signOut, resendConfirmationEmail } = useAuth();

    const [signingOut, setSigningOut] = useState(false);
    const [deliveryLoading, setDeliveryLoading] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<any>(null);
    const [selectedPickupPoint, setSelectedPickupPoint] = useState<any>(null);

    const loadDeliverySettings = useCallback(async () => {
        if (!user?.auth_user_id) return;

        setDeliveryLoading(true);
        try {
            const { data: settings, error: settingsError } = await supabase
                .from('customer_delivery_settings')
                .select('*')
                .eq('customer_id', user.auth_user_id)
                .maybeSingle();

            if (settingsError) {
                console.error('[Profile] settings error:', settingsError);
                return;
            }

            if (settings) {
                if (settings.delivery_type === 'courier' && settings.address_id) {
                    const { data: address, error: addressError } = await supabase
                        .from('customer_addresses')
                        .select('*')
                        .eq('id', settings.address_id)
                        .maybeSingle();

                    if (!addressError && address) {
                        setSelectedAddress(address);
                        setSelectedPickupPoint(null);
                    }
                } else if (settings.delivery_type === 'pickup' && settings.pickup_point_id) {
                    const { data: pickupPoint, error: pickupError } = await supabase
                        .from('pickup_points')
                        .select('*')
                        .eq('id', settings.pickup_point_id)
                        .maybeSingle();

                    if (!pickupError && pickupPoint) {
                        setSelectedPickupPoint(pickupPoint);
                        setSelectedAddress(null);
                    }
                }
            }
        } catch (err) {
            console.error('[Profile] error loading delivery settings:', err);
        } finally {
            setDeliveryLoading(false);
        }
    }, [user?.auth_user_id]);

    useEffect(() => {
        if (user?.auth_user_id) {
            loadDeliverySettings();
        }
    }, [user?.auth_user_id, loadDeliverySettings]);

    useEffect(() => {
        if (sessionStorage.getItem('is_recovery_flow') === 'true') {
            navigate('/reset-password');
        }
    }, [navigate]);

    const getFullName = () => {
        const fn = user?.first_name?.trim() || '';
        const ln = user?.last_name?.trim() || '';
        if (fn && ln) return `${fn} ${ln}`;
        return fn || ln || 'Пользователь';
    };

    const handleSignOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            // Используем signOut из AuthContext — он сам чистит стейт и редиректит
            await signOut();
        } catch (err) {
            console.error('Sign out error:', err);
            toast.error('Ошибка при выходе');
            setSigningOut(false);
        }
    };

    const handleResendEmail = async () => {
        if (!user?.email) return;
        const result = await resendConfirmationEmail(user.email);
        if (!result.success && result.error) {
            toast.error(result.error);
        }
    };

    const requireAuth = (path: string) => {
        if (!user) {
            navigate('/login', { state: { from: location } });
        } else {
            navigate(path);
        }
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Загрузка...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">👤</div>
                <h3>Войдите в аккаунт</h3>
                <p>Чтобы просмотреть профиль, войдите в аккаунт</p>
                <button
                    onClick={() => navigate('/login', { state: { from: location } })}
                    className="auth-button"
                >
                    Войти
                </button>
            </div>
        );
    }

    return (
        <div className="profile-container-mobile">
            <div className="profile-header-mobile">
                <div className="profile-avatar-mobile">
                    {getFullName().charAt(0).toUpperCase()}
                </div>
                <h2 className="profile-name">{getFullName()}</h2>

                <div className="profile-email-status">
                    <p className="profile-email">{user.email || 'Email не указан'}</p>
                    {user.email && (
                        user.email_confirmed ? (
                            <span className="email-confirmed-badge">
                                <CheckCircleIcon style={{ width: 16, height: 16 }} />
                                Подтвержден
                            </span>
                        ) : (
                            <button
                                className="email-unconfirmed-badge"
                                onClick={handleResendEmail}
                            >
                                <XCircleIcon style={{ width: 16, height: 16 }} />
                                Отправить письмо
                            </button>
                        )
                    )}
                </div>
            </div>

            <div className="profile-info-mobile">
                <div className="info-row">
                    <UserCircleIcon style={{ width: 20, height: 20 }} />
                    <div>
                        <div className="info-label">Имя и фамилия</div>
                        <div className="info-value">{getFullName()}</div>
                    </div>
                </div>
                <div className="info-row">
                    <EnvelopeIcon style={{ width: 20, height: 20 }} />
                    <div>
                        <div className="info-label">Email</div>
                        <div className="info-value">{user.email || 'Не указан'}</div>
                    </div>
                </div>
                <div className="info-row">
                    <GlobeAltIcon style={{ width: 20, height: 20 }} />
                    <div>
                        <div className="info-label">Страна доставки</div>
                        <div className="info-value">{getCountryLabel(user.country_code || 'TJ')}</div>
                    </div>
                </div>
                <div className="info-row">
                    <PhoneIcon style={{ width: 20, height: 20 }} />
                    <div>
                        <div className="info-label">Телефон</div>
                        <div className="info-value">
                            {user.phone_number
                                ? getDisplayPhone(user.phone_number, user.phone_country_code || '+992')
                                : 'Не указан'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-actions">
                <button onClick={() => navigate('/profile/edit')} className="edit-button">
                    <PencilIcon style={{ width: 18, height: 18 }} /> Редактировать профиль
                </button>
            </div>

            <button onClick={() => requireAuth('/orders')} className="profile-menu-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
                <span>Мои заказы</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>

            <button onClick={() => requireAuth('/wishlist')} className="profile-menu-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <span>Избранное</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>

            <div className="delivery-settings-section">
                <h3>Настройки доставки</h3>
                <div className="delivery-settings-preview">
                    {deliveryLoading ? (
                        <div className="delivery-preview loading">
                            <span>Загрузка...</span>
                        </div>
                    ) : selectedAddress ? (
                        <div className="delivery-preview">
                            <TruckIcon style={{ width: 20, height: 20 }} />
                            <span>
                                Курьер: {selectedAddress.city}, {selectedAddress.street}, {selectedAddress.house}
                            </span>
                        </div>
                    ) : selectedPickupPoint ? (
                        <div className="delivery-preview">
                            <BuildingStorefrontIcon style={{ width: 20, height: 20 }} />
                            <span>Пункт выдачи: {selectedPickupPoint.name}</span>
                        </div>
                    ) : (
                        <div className="delivery-preview empty">
                            <span>Способ доставки не выбран</span>
                        </div>
                    )}
                    <button
                        className="edit-delivery-btn"
                        onClick={() => requireAuth('/delivery-selection')}
                    >
                        <PencilIcon style={{ width: 16, height: 16 }} />
                        Выбрать способ доставки
                    </button>
                </div>
            </div>

            <div className="profile-actions">
                <button
                    onClick={handleSignOut}
                    className="logout-button"
                    disabled={signingOut}
                >
                    {signingOut ? 'Выход...' : 'Выйти'}
                </button>
            </div>
        </div>
    );
};